import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  Users, Database, GitMerge, ShieldCheck, FileText, ListChecks, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const GroupsManagementPage = () => {
  const benefits = [
    {
      icon: GitMerge,
      title: 'Policy-based approvals with configurable ownership chains',
      description:
        'Define who can approve DL and group changes - by ownership, team, or security policy - with automated routing, reminders, and escalation when approvers do not respond.',
    },
    {
      icon: ListChecks,
      title: 'Enterprise audit logging and rollback controls',
      description:
        'Every change is logged with requester, approver, timestamp, and exact modification - and rollback is supported for changes that need to be reversed quickly.',
    },
    {
      icon: Users,
      title: 'End-to-end automation across IAM and email systems',
      description:
        'The full lifecycle - request, approval, execution in Exchange and Azure AD, ITSM update, and notification - is handled without manual IT involvement for standard cases.',
    },
  ];

  const capabilities = [
    'Integrate Exchange/M365 + Azure AD Groups + ITSM + approval engine',
    'Define ownership rules: who can approve changes and when',
    'Configure flows: add/remove member, create, and modify settings',
    'Add governance: RBAC + audit log + rollback controls',
    'Pilot common DL workflows → expand to groups and access',
  ];

  const features = [
    {
      icon: Users,
      title: 'DL Member Management',
      desc: 'Add and remove distribution list members, update display names, and modify DL settings - automatically, with policy validation before every change.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: Database,
      title: 'Azure AD Group Sync',
      desc: 'Manage Azure AD security groups and Microsoft 365 groups in sync with Exchange DLs - ensuring consistent membership across email and IAM systems.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: GitMerge,
      title: 'Approval Chain Orchestration',
      desc: 'Route DL and group change requests through configured approval chains - with multi-level approvals, SLA tracking, and automatic escalation.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: ShieldCheck,
      title: 'RBAC Enforcement',
      desc: 'Enforce role-based access controls on who can request and approve group changes - preventing unauthorized modifications to sensitive distribution lists.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: FileText,
      title: 'ITSM Auto-Update',
      desc: 'Every DL or group change generates and updates an ITSM ticket - with status tracking from request to completion and automatic closure on success.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: ListChecks,
      title: 'Audit Log & Rollback',
      desc: 'A complete log of every change - who requested it, who approved it, what was modified, and when - with rollback capability for reversals within defined windows.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'User requests DL or group action',
      desc: 'The user submits an add-member, remove-member, create, or modify request via voice, chat, or the ITSM portal - specifying the target DL or group and the required change.',
    },
    {
      step: '02',
      title: 'AI validates DL name, members, justification, and ownership',
      desc: 'The agent validates the target DL or group exists, that the requester has permission to request the change, and that all required justification is provided.',
    },
    {
      step: '03',
      title: 'Routes approvals if required',
      desc: 'If the change requires approval - per configured ownership or RBAC rules - the request is routed to the appropriate approver(s) with reminders and escalation built in.',
    },
    {
      step: '04',
      title: 'Executes action in Exchange and Azure AD',
      desc: 'Once approved, the change is executed via Exchange and Azure AD APIs - member added or removed, group created, or settings updated - instantly and accurately.',
    },
    {
      step: '05',
      title: 'Updates ITSM and notifies requester',
      desc: 'The ITSM ticket is updated to completed status and the requester receives a notification confirming the change, including any relevant access details.',
    },
  ];

  const stats = [
    { value: '~45%', label: 'Ticket deflection' },
    { value: '~40%', label: 'L1 workload reduction' },
    { value: '<$5', label: 'Cost per ticket (vs $25–$40)' },
    { value: '24/7', label: 'Fulfillment coverage' },
  ];

  const faqs = [
    {
      q: 'Can approvals require the business owner of the distribution list?',
      a: 'Yes. Ownership is configurable at the DL or group level. When a change is requested, the system identifies the configured owner(s) and routes the approval to them - not just the requester\'s manager. Multiple approvers and sequential approval chains are both supported.',
    },
    {
      q: 'Does it support hybrid Exchange environments?',
      a: 'Yes. Digian.ai supports hybrid Exchange environments where on-premises Exchange and Exchange Online coexist. The system identifies where the DL is authoritative and executes the change in the correct environment, with synchronization handled by the existing hybrid configuration.',
    },
    {
      q: 'Can it handle bulk member additions to a distribution list?',
      a: 'Yes. Bulk member additions or removals can be submitted as a structured request - for example, adding an entire team to a DL. The system validates each member, applies ownership and RBAC checks, and executes the bulk change in a single operation with a full audit record.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseGroupsManagement" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Groups Management","item":"https://digian.ai/use-cases/groups-management"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">IT Helpdesk / Distribution Lists &amp; Groups Management</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Automated DL &amp; Group Management - Fast, Governed, Auditable
            </h1>
            <p className="text-lg text-slate-600">
              Automate DL and group tasks: add and remove members, create groups, modify settings, handle approvals, and update ITSM automatically - across Exchange, M365, and Azure AD.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['Exchange + Azure AD integration', 'Configurable approval chains', 'Full audit log + rollback'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">DL &amp; Groups Automation</p>
                <p className="text-sm text-slate-600">Governed self-service for Exchange and Azure AD groups</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <Users className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why IT teams automate DL and group management</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate DL and group governance</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How DL and group management automation works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">DL and group requests fulfilled - without an IT ticket</h2>
            <p className="text-xl text-indigo-100">
              Governed, auditable, and instant. From request to Exchange and Azure AD - fully automated.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">Exchange + Azure AD · Approval chains · Audit log + rollback</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GroupsManagementPage;
